import React, { useState } from 'react';
import { BarChart, Bar, XAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList, ReferenceLine } from 'recharts';

const CURRENCY_SYMBOLS = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥'
};

const PortfolioGrowthChart = ({ 
    performanceData = [], 
    currency = 'EUR',
    chartColor = 'var(--primary-color)',
    height = 300,
    selectedRange = 'year'
}) => {
    const [showPercentage, setShowPercentage] = useState(true);
    
    // Calculate average
    const average = performanceData.length > 0 
        ? performanceData.reduce((sum, item) => sum + item.value, 0) / performanceData.length
        : 0;

    // Format currency values
    const formatCurrencyValue = (value, currencyCode) => {
        if (value === null || value === undefined) return 'N/A';
        const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
        return `${symbol}${value.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    // Format the average value for display
    const formattedAverage = (() => {
        const value = Math.abs(average);
        if (value >= 1000) {
            return formatCurrencyValue(value / 1000, currency) + 'k';
        }
        return formatCurrencyValue(value, currency);
    })();

    // Calculate X-axis label interval based on data length
    const calculateXAxisInterval = Math.max(1, Math.floor(performanceData.length / 7));

    if (!performanceData || performanceData.length === 0) {
        return <div style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p>Keine Daten verfügbar</p>
        </div>;
    }

    return (
        <div style={{ height: `${height}px`, width: '100%', minWidth: '0', padding: '0 0.25rem' }}>
            <ResponsiveContainer>
                <BarChart 
                    data={performanceData} 
                    margin={{ top: 5, right: 5, left: 15, bottom: 5 }}
                    barSize={30}
                >
                    <defs>
                        <linearGradient id="gradient-positive" x1="0" y1="0" x2="0" y2="1">
                            <stop
                                offset="0%"
                                stopColor="var(--success-color)"
                                stopOpacity={0.8}
                            />
                            <stop
                                offset="100%"
                                stopColor="var(--success-color)"
                                stopOpacity={0.2}
                            />
                        </linearGradient>
                        <linearGradient id="gradient-negative" x1="0" y1="0" x2="0" y2="1">
                            <stop
                                offset="0%"
                                stopColor="var(--danger-color)"
                                stopOpacity={0.8}
                            />
                            <stop
                                offset="100%"
                                stopColor="var(--danger-color)"
                                stopOpacity={0.2}
                            />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis 
                        dataKey="period" 
                        stroke="var(--text-secondary)"
                        angle={-45}
                        textAnchor="end"
                        interval={calculateXAxisInterval}
                        tick={{
                            fontSize: 11,
                            dx: 10
                        }}
                    />
                    {/* Average line */}
                    <ReferenceLine
                        y={average}
                        stroke="var(--text-secondary)"
                        strokeDasharray="3 3"
                    />
                    {/* Top right label */}
                    <ReferenceLine
                        y={performanceData[0]?.value || 0}
                        label={{
                            value: `Ø ${formattedAverage}`,
                            position: 'insideTopRight',
                            fill: 'var(--text-secondary)',
                            fontSize: 11
                        }}
                        stroke="none"
                    />
                    <Tooltip 
                        content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                const isPositive = data.isPositive;
                                
                                return (
                                    <div style={{ 
                                        backgroundColor: 'var(--card-bg)',
                                        border: `1px solid ${isPositive ? 'var(--success-color)' : 'var(--danger-color)'}`,
                                        padding: '0.5rem',
                                        fontSize: '0.875rem',
                                        color: 'var(--text-primary)',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        <div style={{ marginBottom: '0.25rem' }}>{data.period}</div>
                                        <div style={{ 
                                            color: isPositive ? 'var(--success-color)' : 'var(--danger-color)'
                                        }}>
                                            {formatCurrencyValue(data.value, currency)} ({data.percentageGrowth.toFixed(2)}%)
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        }}
                        cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                    />
                    <Bar 
                        dataKey="value"
                        radius={[4, 4, 0, 0]}
                    >
                        {performanceData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={`url(#gradient-${entry.isPositive ? 'positive' : 'negative'})`}
                                style={{ filter: 'none' }}
                                className="bar-cell"
                            />
                        ))}
                        <LabelList
                            dataKey="value"
                            content={({ x, y, width, value, payload }) => {
                                if (typeof value === 'undefined') return null;
                                if (!payload) return null;
                            
                                const isPositive = payload.isPositive;
                                const yOffset = isPositive ? -10 : 20;
                                const displayValue = showPercentage 
                                    ? `${payload.percentageGrowth.toFixed(1)}%`
                                    : `${Math.abs(value).toFixed(0)}${CURRENCY_SYMBOLS[currency] || currency}`;
                            
                                return (
                                    <g onClick={() => setShowPercentage(!showPercentage)} style={{ cursor: 'pointer' }}>
                                        <text
                                            x={x + width / 2}
                                            y={y + yOffset}
                                            fill="var(--text-primary)"
                                            textAnchor="middle"
                                            fontSize="11"
                                            fontWeight="500"
                                        >
                                            {isPositive ? '↑' : '↓'} {displayValue}
                                        </text>
                                    </g>
                                );
                            }}
                            position="top"
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default PortfolioGrowthChart;